// Components
import { SMPContainer, SMPText, SMPImage, SMPFlex, StyledSMPContainerVariants, ConfigContext } from '../../../index';

// Types
import { INormalizedTournamentData, ITournament, SportType } from '../../../../types';
import { DOMESTIC } from '../../../../constants/sportRegions';
import { Sports } from '../../../../constants/sportType';
import { StageTypes } from '../../../../constants/stageType';
import { useContext } from 'react';

export interface ITournamentProps {
  tournament?: INormalizedTournamentData;
  handleTournamentClick?: (tournament: ITournament) => void;
  sport?: SportType;
  hideLogo?: boolean;
  hideStandingsIcon?: boolean;
  hideSubName?: boolean;
  isMainImageRound?: boolean;
  variant?: StyledSMPContainerVariants['variant'];
}

const getTournamentName = ({ tournament, sport }: Pick<ITournamentProps, 'tournament' | 'sport'>) => {
  if (sport === Sports.FOOTBALL && tournament?.hasMultipleStages) {
    return tournament?.stage?.name;
  }

  if (sport === Sports.FOOTBALL && !tournament?.hasMultipleStages && !tournament?.isDomestic && tournament?.stage) {
    return tournament?.stage?.type === StageTypes.KNOCK_OUT && tournament?.round?.name ? tournament?.round?.name : tournament?.stage?.name;
  }

  if (sport === Sports.FOOTBALL && !tournament?.hasMultipleStages && !tournament?.isDomestic && !tournament?.stage) {
    return tournament?.name;
  }

  if (sport === Sports.TENNIS && tournament?.season) {
    return `${tournament.name}-${tournament.season}`;
  }

  return tournament?.name;
};

const getTournamentSubName = ({ tournament, sport }: Pick<ITournamentProps, 'tournament' | 'sport'>) => {
  if (sport === Sports.TENNIS) return tournament?.country?.name;
  if (sport === Sports.FOOTBALL && tournament?.hasMultipleStages) return tournament.name;
  if (sport === Sports.FOOTBALL && !tournament?.isDomestic && tournament?.stage?.type === StageTypes.FRIENDLY)
    return tournament?.country?.name;

  if (sport === Sports.FOOTBALL && !tournament?.hasMultipleStages && !tournament?.stage) {
    return tournament?.country?.name;
  }

  return tournament?.region === DOMESTIC ? tournament?.country?.name : tournament?.name;
};

export const Tournament = ({
  tournament,
  handleTournamentClick,
  sport,
  hideLogo,
  hideStandingsIcon,
  hideSubName,
  variant = 'livescoreTournamentContainer',
  isMainImageRound,
}: ITournamentProps): JSX.Element => {
  const { dataLabels } = useContext(ConfigContext);
  const onTournamentClick = () => tournament && handleTournamentClick && handleTournamentClick(tournament);

  const getLogo = () => {
    if (tournament?.logo && !isMainImageRound) {
      return tournament?.logo;
    }

    if (tournament?.country?.assets?.flag?.url) {
      return tournament?.country?.assets?.flag?.url;
    }

    return '';
  };

  const isFootballSingleEventTournamentContainer = variant === 'footballSingleEventTournamentContainer';

  return (
    <SMPContainer size='4' variant={variant} css={{ width: isFootballSingleEventTournamentContainer ? '100%' : undefined, p: 'unset' }}>
      <SMPFlex
        grid
        variant='tournament'
        css={{
          '& > a': { p: variant === 'livescoreTournamentContainer' ? '$2 $3' : undefined },
          '& > a:first-child': { pr: variant === 'livescoreTournamentContainer' ? 'unset' : undefined },
          alignItems: 'end',
        }}
      >
        <a href={tournament?.tournamentLink} target={tournament?.urlTarget} style={{ textDecoration: 'none' }}>
          <SMPFlex
            onClick={onTournamentClick}
            gap='md'
            justify={variant === 'livescoreTournamentContainer' ? 'start' : 'center'}
            align={'center'}
          >
            {!hideLogo && tournament?.logo && (
              <SMPImage
                css={{ br: isMainImageRound || !tournament?.logo ? '$round' : '' }}
                variant={isMainImageRound || !tournament?.logo ? 'borderedLivescoreEntityLogo' : 'tournamentLogo'}
                src={getLogo()}
                alt={`${tournament?.name} Logo`}
              />
            )}
            <SMPFlex variant='column' gap='md' style={{ width: isFootballSingleEventTournamentContainer ? '100%' : undefined }}>
              <SMPText
                variant='contrast'
                size='3'
                weight='700'
                textAlign={isFootballSingleEventTournamentContainer ? 'center' : undefined}
                style={{ lineHeight: isFootballSingleEventTournamentContainer ? '20px' : 'normal' }}
              >
                {getTournamentName({ tournament, sport })}
              </SMPText>
              {hideSubName ? null : (
                <SMPText variant='contrast' size='2' style={{ lineHeight: isFootballSingleEventTournamentContainer ? '20px' : undefined }}>
                  {getTournamentSubName({ tournament, sport })}
                </SMPText>
              )}
            </SMPFlex>
          </SMPFlex>
        </a>

        {hideStandingsIcon ? null : (
          <a href={tournament?.standingsLink} target={tournament?.urlTarget} style={{ textDecoration: 'none' }}>
            <SMPText
              size='2'
              onClick={onTournamentClick}
              css={{
                textDecoration: 'underline',
                textDecorationColor: '$standingsLinkColor',
                marginLeft: '$2_5',
                color: '$standingsLinkColor',
              }}
            >
              {dataLabels?.standings ?? 'Standings'}
            </SMPText>
          </a>
        )}
      </SMPFlex>
    </SMPContainer>
  );
};
